import {
  easiestVoicing,
  layoutShape,
  mirrorLayout,
  voicingsFor,
  type FrettedInstrument,
  type ShapeLayout,
} from '@/core/instruments/shapes';
import type { Chord } from '@/core/theory/chord';
import { mod } from '@/core/util/math';
import type { DisplayOptions } from '@/core/theory/display';

/** String spacing per instrument in px (design: guitar 32, ukulele 40). */
export const STRING_SPACING: Record<FrettedInstrument, number> = {
  guitar: 32,
  ukulele: 40,
  mandolin: 40,
  bass: 40,
};

/** How the player wants shapes chosen (F4.9 voicing choice, F5.4 easy mode). */
export interface VoicingPrefs {
  choices: Record<string, number>;
  easy: boolean;
}

export const NO_VOICING_PREFS: VoicingPrefs = { choices: {}, easy: false };

/** Key of a remembered voicing choice: the shape as played (after transpose and capo). */
export function voicingKey(instrument: FrettedInstrument, shapeRoot: number, chord: Chord): string {
  return `${instrument}:${mod(shapeRoot, 12)}:${chord.quality}`;
}

/** The shape root for a sounding chord: sounding + transpose − capo. */
export function shapeRootOf(chord: Chord, o: DisplayOptions): number {
  return mod(chord.root + o.transpose - o.capo, 12);
}

/** The fingering to show: the player's choice, else the easiest in easy mode, else the default. */
export function fretsFor(
  chord: Chord,
  instrument: FrettedInstrument,
  o: DisplayOptions,
  prefs: VoicingPrefs,
): number[] {
  const root = shapeRootOf(chord, o);
  const all = voicingsFor(instrument, root, chord.quality);
  const chosen = prefs.choices[voicingKey(instrument, root, chord)];
  if (chosen !== undefined && all[chosen]) return all[chosen];
  return prefs.easy ? easiestVoicing(instrument, root, chord.quality) : (all[0] ?? []);
}

/** Diagram layout for a sounding chord. */
export function layoutFor(
  chord: Chord | null,
  instrument: FrettedInstrument,
  o: DisplayOptions,
  leftHanded: boolean,
  prefs: VoicingPrefs = NO_VOICING_PREFS,
): ShapeLayout | null {
  if (!chord) return null;
  const layout = layoutShape(fretsFor(chord, instrument, o, prefs));
  return leftHanded ? mirrorLayout(layout) : layout;
}
